/**
 * Template de prise de besoin : Procédure de réponse aux incidents de sécurité (NIST) v1
 * Aligné sur NIST CSF 2.0, ITIL 4 et ISO 27001
 * Procédure opérationnelle détaillée pour la réponse aux incidents de sécurité
 */

import type { IntakeTemplate } from '../templateTypes';

export const templateProcedureReponseIncidentsSecuriteV1: IntakeTemplate = {
  id: 'procedure-reponse-incidents-securite-v1',
  name: 'Prise de besoin Procédure de réponse aux incidents de sécurité (NIST) v1',
  description:
    'Questionnaire complet pour élaborer une procédure de réponse aux incidents de sécurité conforme NIST CSF 2.0, ITIL 4 et ISO 27001. Définit les étapes, workflows et actions pour la détection, analyse, confinement, éradication et restauration des incidents de sécurité.',
  documentTypeCode: 'SECURITY_INCIDENT_RESPONSE_PROCEDURE',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Détection & Analyse',
      description: 'Processus de détection et d\'analyse des incidents de sécurité',
      icon: 'Eye',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour la procédure.',
        },
        {
          id: 'sources_detection',
          label: 'Quelles sont les sources de détection d\'incidents de sécurité ?',
          type: 'multiselect',
          options: [
            'SIEM (Security Information and Event Management)',
            'Alertes antivirus',
            'Alertes firewall',
            'Alertes IDS/IPS',
            'Rapports utilisateurs',
            'Alertes cloud (Microsoft 365, AWS)',
            'Surveillance réseau',
            'Audits de sécurité',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (DE.CM): Sources de détection pour identification rapide.',
        },
        {
          id: 'processus_detection',
          label: 'Quel est le processus de détection et d\'analyse ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Réception alerte → Triage → Analyse initiale → Classification sévérité...',
          helpText: 'NIST CSF 2.0 (RS.AN): Processus structuré pour analyse efficace.',
        },
        {
          id: 'outils_analyse',
          label: 'Quels outils sont utilisés pour l\'analyse ?',
          type: 'multiselect',
          options: [
            'SIEM',
            'Outils forensiques',
            'Analyseurs de logs',
            'Outils de réseau',
            'Outils de malware analysis',
            'Outils cloud',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0 (RS.AN): Outils appropriés pour analyse approfondie.',
        },
        {
          id: 'ton_document_procedure',
          label: 'Quel ton souhaitez-vous pour cette procédure ?',
          type: 'select',
          options: [
            'Formel et procédural (ton impératif, étapes claires)',
            'Pédagogique et explicatif (ton guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la facilité d\'utilisation de la procédure.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Confinement & Éradication',
      description: 'Processus de confinement et d\'éradication',
      icon: 'Shield',
      questions: [
        {
          id: 'mesures_confinement',
          label: 'Quelles mesures de confinement sont prévues ?',
          type: 'multiselect',
          options: [
            'Isolation réseau',
            'Déconnexion de systèmes',
            'Blocage d\'accès',
            'Désactivation de comptes',
            'Modification de règles firewall',
            'Mise en quarantaine',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (RS.MI): Mesures de confinement pour limiter l\'impact.',
        },
        {
          id: 'processus_confinement',
          label: 'Quel est le processus de confinement ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Évaluation → Décision → Mise en œuvre → Vérification...',
          helpText: 'NIST CSF 2.0 (RS.MI): Processus structuré pour confinement efficace.',
        },
        {
          id: 'mesures_eradication',
          label: 'Quelles mesures d\'éradication sont prévues ?',
          type: 'multiselect',
          options: [
            'Suppression de malware',
            'Correction de vulnérabilités',
            'Réinitialisation de systèmes',
            'Nettoyage de données',
            'Mise à jour de sécurité',
            'Modification de configurations',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (RS.MI): Mesures d\'éradication pour éliminer la menace.',
        },
        {
          id: 'processus_eradication',
          label: 'Quel est le processus d\'éradication ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Identification → Planification → Exécution → Vérification...',
          helpText: 'NIST CSF 2.0 (RS.MI): Processus structuré pour éradication complète.',
        },
        {
          id: 'verification_eradication',
          label: 'Comment vérifie-t-on que l\'éradication est complète ?',
          type: 'textarea',
          placeholder: 'Décrivez les méthodes de vérification: scans, tests, audits...',
          helpText: 'NIST CSF 2.0 (RS.MI): Vérification essentielle pour confirmer l\'éradication.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Restauration & Post-Incident',
      description: 'Processus de restauration et activités post-incident',
      icon: 'CheckCircle',
      questions: [
        {
          id: 'strategie_restauration',
          label: 'Quelle est la stratégie de restauration ?',
          type: 'select',
          options: [
            'Restauration depuis sauvegardes',
            'Reconstruction complète',
            'Restauration progressive',
            'Basculement vers site de secours',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4 (ITSCM): Stratégie de restauration pour rétablir les services.',
        },
        {
          id: 'processus_restauration',
          label: 'Quel est le processus de restauration ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Préparation → Restauration → Tests → Validation → Remise en service...',
          helpText: 'NIST CSF 2.0 (RS.IM): Processus structuré pour restauration efficace.',
        },
        {
          id: 'delais_restauration',
          label: 'Quels sont les délais cibles de restauration (RTO) ?',
          type: 'textarea',
          placeholder: 'Ex: Services critiques: 4 heures, Services standards: 24 heures...',
          helpText: 'ITIL 4 (ITSCM): Délais cibles pour restauration (RTO).',
        },
        {
          id: 'activites_post_incident',
          label: 'Quelles activités post-incident sont prévues ?',
          type: 'multiselect',
          options: [
            'Rapport d\'incident',
            'Analyse de cause racine',
            'Leçons apprises',
            'Amélioration des procédures',
            'Formation supplémentaire',
            'Mise à jour du plan',
            'Autre',
          ],
          helpText: 'ITIL 4: Activités post-incident pour amélioration continue.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Documentation & Amélioration',
      description: 'Documentation et amélioration continue',
      icon: 'FileText',
      questions: [
        {
          id: 'documentation_incident',
          label: 'Quelle documentation doit être créée pour chaque incident ?',
          type: 'multiselect',
          options: [
            'Rapport d\'incident',
            'Journal de gestion',
            'Analyse de cause racine',
            'Plan d\'amélioration',
            'Métriques de performance',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0: Documentation essentielle pour traçabilité et amélioration.',
        },
        {
          id: 'metriques_performance',
          label: 'Quelles métriques de performance sont suivies ?',
          type: 'multiselect',
          options: [
            'MTTR (Mean Time To Resolve)',
            'MTTD (Mean Time To Detect)',
            'Taux de résolution première ligne',
            'Taux de satisfaction client',
            'Nombre d\'incidents par type',
            'Autre',
          ],
          helpText: 'ITIL 4: Métriques pour mesurer la performance.',
        },
        {
          id: 'revision_procedure',
          label: 'À quelle fréquence souhaitez-vous réviser cette procédure ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Après chaque incident majeur', 'À déterminer'],
          helpText: 'NIST CSF 2.0: Révision régulière essentielle pour maintenir l\'efficacité.',
        },
      ],
    },
  ],
};

